import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { Toolbar } from 'components/Toolbar';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { ProjectStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { updateContractGql } from 'pages/Project/UerProject/Contract/ContractQueries';
import { Button, cnMixSpace } from 'uikit';

import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface IContractUpdateToolbar {
    formRef: React.RefObject<any>;
    isCreate?: boolean;
    isLoading?: boolean;
    isDisabledCancel?: boolean;
    resetData?: (data: any) => any;
    statusProject?: ProjectStatusEnum;
}

export const ContractUpdateToolbar: React.FC<IContractUpdateToolbar> = (props) => {
    const { formRef, isCreate, isLoading, isDisabledCancel, resetData, statusProject } = props;

    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const { project: contractRules } = useAccess();
    const navigate = useNavigate();
    const [isOpenDialogStatus, setIsOpenDialogStatus] = useState<boolean>(false);

    const [, updateContractStatus] = useRequest({
        requestId: 'updateContract',
        query: updateContractGql,
        withEffect: false,
    });

    const handleSave = useCallback(() => {
        formRef?.current?.submit();

        sendGpnAnalyticsEvent({
            componentId: 'rim_contract save button_pressed',
            projectId: projectData?.uniqueId,
            contractId: projectData?.contractId,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        });
    }, [
        formRef,
        projectData?.contractId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectData?.uniqueId,
        sendGpnAnalyticsEvent,
    ]);

    const handleCancel = useCallback(() => {
        if (formRef?.current?.isDirty) {
            setIsOpenDialogStatus(true);
        } else {
            resetData?.(null);
            navigate(isCreate ? '../../' : `../`);
        }
    }, [formRef, isCreate, navigate, resetData]);

    const handleSubmitDialogStatus = useCallback(() => {
        setIsOpenDialogStatus(false);
        resetData?.(null);
        navigate(isCreate ? '../../' : `../`);
    }, [isCreate, navigate, resetData]);

    const handleCancelDialogStatus = useCallback(() => {
        setIsOpenDialogStatus(false);
    }, []);

    useEffect(
        () => () => {
            setIsOpenDialogStatus(false);
        },
        [],
    );

    const label = useMemo(() => (isCreate ? 'Создать' : 'Сохранить'), [isCreate]);

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

    const disabledButton = isCreate ? !contractRules?.canCreate : !contractRules?.canSave;

    return (
        <Toolbar fullLine>
            <Button
                label={label}
                onClick={handleSave}
                loading={updateContractStatus === RequestStatus.FETCHING || isLoading}
                size="s"
                disabled={disabledButton || isProjectStatusDelayed}
            />

            <Button
                label="Отменить"
                onClick={handleCancel}
                view="ghost"
                disabled={updateContractStatus === RequestStatus.FETCHING || isDisabledCancel}
                className={cnMixSpace({ mL: 's' })}
                size="s"
            />

            <ConfirmationDialog
                textHeader="отмена изменений"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenDialogStatus}
                onSubmit={handleSubmitDialogStatus}
                onClose={handleCancelDialogStatus}
            />
        </Toolbar>
    );
};

ContractUpdateToolbar.displayName = 'ContractUpdateToolbar';
